<script context="module">
  export const ACCOUNT_CARD_FRAGMENT = gql`
    fragment AccountCardFragment on API_Account {
      id
      name
      slug
      bio
      metafyPartner
      metafyPartnerType
      languages
      sessionPriceValue
      avatar {
        original {
          url
        }
      }
      cover {
        original {
          url
        }
      }
      sessionCancelationPolicy {
        id
        enabled
        amount
        policyType
        timeFrame
        timeUnit
      }
      gamesOffered(first: 1) {
        totalCount
        edges {
          node {
            id
            slug
            localizedTitles {
              en
            }
            logoMain {
              original {
                url
              }
            }
          }
        }
      }
      ownedGroup {
        id
      }
    }
  `;
</script>

<script lang="ts">
  import type { Api_Account, Api_Game } from '$lib/types/api/graphql';

  import { extractNodes, lazyComponent } from '$lib/utils';
  import { gql } from '$lib/apollo';

  import { Image, ProgressiveImage } from '$mds/Image';
  import Avatar from '$components/Avatar.svelte';
  import Button from '$mds/Buttons/Button/Button.svelte';
  import Suspense from '$components/Suspense.svelte';
  import { LANGUAGE_EMOJI_MAP } from '$lib/data/languages';
  import IconPlacement1 from './placement_1.svg?component';
  import IconPlacement2 from './placement_2.svg?component';
  import IconPlacement3 from './placement_3.svg?component';
  import BlurImage from '$mds/Image/BlurImage.svelte';
  import { tooltip } from '$lib/directives/tooltip';
  import { goto } from '$app/navigation';
  import { observe } from '$lib/directives/observer';
  import ReschedulePolicyInfo from '$components/ReschedulePolicyInfo.svelte';
  import MetafyPartnerBadge from '$components/icons/MetafyProBadge/MetafyPartnerBadge.svelte';
  import { activeProfileDrawerSlug } from '$routes/(nav)/_components/Navigation/stores';

  const CoachingProductsDrawerLazy = lazyComponent(() => import('../../../sessions/CoachingProductsDrawer.svelte'));

  export let account: Api_Account;
  export let placement: number | null = null;
  export let hidePricing = false;
  export let hideGames = false;
  export let isExpanded = false;
  export let redirectToProfile = false;
  export let game: Api_Game | null = null;

  let className = '';
  export { className as class };

  let isSessionsDrawerOpen = false;

  let isInView = false;

  const isPro = account.metafyPartner ?? false;
  const restGamesCount = account.gamesOffered?.totalCount - 1;

  function formatLanguages(languages) {
    if (languages.length === 1) {
      return languages[0];
    }
    const allButLast = languages.slice(0, -1).join(', ');
    const last = languages[languages.length - 1];
    return `${allButLast} and ${last}`;
  }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
<div
  role="listitem"
  class="@container z-1 isolate flex transform-gpu cursor-pointer flex-col {isExpanded
    ? ''
    : 'delay-300 hover:z-[2] hover:delay-0'} {className}"
  on:click={() => {
    if (!redirectToProfile) {
      isSessionsDrawerOpen = true;
    } else {
      goto(`/@${account.slug}/overview`);
    }
  }}
>
  <div
    use:observe={{ threshold: 0.2 }}
    on:enterview={() => (isInView = true)}
    on:exitview={() => (isInView = false)}
    class="relative flex-auto transform-gpu {isExpanded
      ? ''
      : 'duration-400 group transition-[transform,opacity,visibility] ease-in-out md:hover:translate-y-[-50px] md:hover:scale-[1.08] '}"
    class:opacity-0={!isInView}
    class:invisible={!isInView}
    class:isExpanded
  >
    <div class="bg-ondark-medium duration-400 isolate flex h-full flex-col rounded-lg">
      <div class="relative -mb-2 h-0 shrink-0 overflow-hidden rounded-t-lg pt-[40%]">
        {#if account.cover?.original?.url}
          <ProgressiveImage
            class="h-full w-full object-cover"
            wrapClass="bg-ondark-medium absolute left-0 top-0 h-full w-full object-cover"
            src={account.cover?.original?.url}
            fit="cover"
            sizes={{ xs: 400 }}
            alt="Cover image"
          />
        {:else}
          <BlurImage
            class="absolute inset-[-10%] h-[120%] w-[120%] max-w-none object-cover"
            src={account.avatar?.original?.url}
            width={6}
            blur={6}
          />
        {/if}

        {#if game && !hideGames}
          <div class="@xs:top-3 @xs:left-3 @xs:right-3 absolute left-2 right-2 top-2 h-8 overflow-hidden rounded-full">
            <span
              class="text-body-xs md:text-body-3xs text-ondark-secondary bg-ondark-medium inline-flex h-8 max-w-full items-center gap-2 rounded-full p-1 pl-9 pr-3 {isExpanded
                ? ''
                : 'md:-translate-x-full md:opacity-0 md:transition-[transform,opacity] md:duration-500 md:ease-in-out md:group-hover:translate-x-0 md:group-hover:opacity-100'}"
            >
              <span class="shrink overflow-hidden text-ellipsis whitespace-nowrap">
                {game?.localizedTitles?.en}
              </span>
              {#if restGamesCount > 0}
                <span class="shrink-0">
                  + {restGamesCount} more
                </span>
              {/if}
            </span>

            <span class="bg-ondark-medium absolute left-0 top-0 block size-8 rounded-full p-1">
              <Image
                class="size-6 shrink-0 rounded-full"
                src={game?.logoMain?.original?.url}
                sizes={{ xs: 40 }}
                alt="Game logo"
              />
            </span>
          </div>
        {/if}
      </div>

      <div class="bg-ondark-low z-1 relative flex flex-1 flex-col rounded-lg p-4">
        <div class="relative -mt-10 flex items-end">
          <Avatar class="bg-ondark-high size-16" user={account} rounded="rounded-full" sizes={{ xs: 128 }} />

          {#if placement && placement > 0 && placement <= 3}
            <div class="@xs:flex relative ml-4 hidden size-8">
              <div class="size-18 absolute left-1/2 top-1/2 ml-0.5 mt-0.5 -translate-x-1/2 -translate-y-1/2">
                {#if placement === 1}
                  <IconPlacement1 class="size-full" />
                {:else if placement === 2}
                  <IconPlacement2 class="size-full" />
                {:else if placement === 3}
                  <IconPlacement3 class="size-full" />
                {/if}
              </div>
            </div>
          {/if}

          <div class="ml-auto flex items-center gap-2">
            {#if account.sessionCancelationPolicy?.enabled}
              <span class="bg-ondark-high z-1 relative inline-flex h-6 items-center rounded px-2">
                <ReschedulePolicyInfo coachProfile={account} iconOnly iconSize="base" />
              </span>
            {/if}

            <!-- TODO: fix emojis font -->
            {#if account.languages.length > 0}
              <span
                use:tooltip={{
                  content: formatLanguages(account.languages),
                  placement: 'top'
                }}
                class="bg-ondark-high z-1 relative inline-flex h-6 items-center rounded px-2"
              >
                <span style:font-family="emoji">
                  {LANGUAGE_EMOJI_MAP[account.languages[0]]}
                </span>
                {#if account.languages.length > 1}
                  <span class="text-body-4xs text-ondark-primary ml-1.5">+{account.languages.length - 1}</span>
                {/if}
              </span>
            {/if}

            {#if !hidePricing}
              <span class="bg-ondark-high inline-flex h-6 items-center rounded px-2">
                <span class="text-ondark-primary text-body-4xs">
                  {account.sessionPriceValue ?? '$$$'}
                </span>
              </span>
            {/if}
          </div>
        </div>

        <div class="mt-5 flex flex-col">
          <span class="text-body-md text-ondark-primary">
            {account.name}

            {#if isPro}
              <MetafyPartnerBadge variant={account.metafyPartnerType} size="xxs" class="ml-2 inline-flex" />
            {/if}
          </span>

          <span class="text-ondark-secondary text-body-2xs">@{account.slug}</span>
        </div>

        <!-- <time class="text-body-3xs text-ondark-primary mt-3 block">{nextAvailability.join(' ')}</time> -->

        <div
          class="duration-400 mt-auto origin-top rounded-b-lg bg-inherit {isExpanded
            ? ''
            : ' md:shadow-newMd transition-[transform] ease-in-out md:absolute md:bottom-2 md:left-0 md:w-full md:translate-y-full md:scale-y-0 md:p-4 md:shadow-neutral-900/50 md:group-hover:scale-y-100 md:group-hover:opacity-100'}"
        >
          <p
            class="text-body-4xs text-ondark-secondary my-3 line-clamp-3 {isExpanded
              ? ''
              : 'md:group-hover:duration-400 delay-0 duration-150 md:my-0 md:opacity-0 md:group-hover:opacity-100 md:group-hover:delay-300 '}"
          >
            {account.bio ?? ''}
          </p>

          <div
            class="mt-4 grid grid-cols-2 gap-3 {isExpanded
              ? ''
              : 'md:group-hover:duration-400 delay-0 duration-150 md:opacity-0 md:group-hover:opacity-100  md:group-hover:delay-200'}"
          >
            <Button
              variant="secondary"
              size="sm"
              width="full"
              on:click={e => {
                e.stopPropagation();
                isSessionsDrawerOpen = true;
              }}
            >
              <span class="@xs:inline hidden">View</span> <span>Sessions</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              width="full"
              on:click={e => {
                if (!redirectToProfile) {
                  e.stopPropagation();
                  $activeProfileDrawerSlug = account.slug;
                }
              }}
            >
              <span class="@xs:inline hidden">View</span> <span>Profile</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<Suspense
  condition={isSessionsDrawerOpen}
  promise={CoachingProductsDrawerLazy}
  let:component={CoachingProductsDrawer}
  spinner
>
  <CoachingProductsDrawer bind:isOpen={isSessionsDrawerOpen} {account} {hidePricing} />
</Suspense>
